import type { KeyboardEvent, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { IS_DEV, isRunningInNativeApp } from '@muzly/shared';

import { PullToRefresh } from '../../../components';
import {
  ConfigTuningPanel,
  DiagnosticsPanel,
  EventLogPanel,
  FirstTimeGuide,
  Glossary,
  InfoTip,
  InputLevelMeterPanel,
  ReadoutPanel,
  RealtimePitchGraphPanel,
  STATUS_TONE,
  ScoringPanel,
  SessionControlPanel,
  SessionControlProvider,
  SessionStickyBar,
  TAB_STORAGE_KEY,
  type TabId,
  nextTabIndex,
  resolveInitialTab,
  useSessionControl,
} from '../components';

/**
 * 네이티브 실시간 음정 엔진 디버그 콘솔의 루트 페이지.
 *
 * 정보구조(#27): 한 컬럼 끝없는 스크롤 대신 패널을 탭으로 그룹화한다 —
 * 라이브(그래프·리드아웃·레벨) · 점수 · 튜닝(config·세션 상태값) · 진단(진단·이벤트로그)
 * · 도움말(첫 사용 가이드·용어집). 세션 제어 sticky 바는 어느 탭에서든 전역 고정(#25).
 * 상태색은 공유 토큰(STATUS_TONE: 정상=green/경고=amber/위험=red)으로 통일한다.
 */

interface PanelMeta {
  id: string;
  title: string;
  hint: string;
  /** 비전문가용 KO 평이체 한 줄 설명. 헤더 "?" 툴팁 + "도움말" 인라인에 쓴다(#13). */
  help: string;
  /** 구현된 패널 본문. 없으면 placeholder를 렌더한다. */
  body?: ReactNode;
  /** 첫 진입 시 펼쳐 둘지. 자주 보는 패널만 true, 세부는 접어 둔다(#20). */
  defaultOpen: boolean;
  /** 탭 안에서 가로 폭 전체를 차지하는 히어로 패널(데스크탑 2열에서 col-span). */
  hero?: boolean;
}

// 라이브 탭 — 실시간 그래프(히어로) + 리드아웃 + 입력 레벨.
const LIVE_PANELS: PanelMeta[] = [
  {
    id: 'graph',
    title: '실시간 음정 그래프',
    hint: '자동 확대 · 음역 · 현재 음 · Energy',
    help: '시간에 따른 음높이 변화를 곡선으로 그립니다. 세로축은 목소리 범위에 맞춰 자동 확대되고, 곡선 색은 음높이, 곡선 끝 초록 점이 현재 음입니다.',
    body: <RealtimePitchGraphPanel />,
    defaultOpen: true,
    hero: true,
  },
  {
    id: 'readout',
    title: '리드아웃',
    hint: 'f0 · midi · note · confidence · voiced · rms',
    help: '지금 들어온 소리의 음높이·음이름·신뢰도·음량을 한눈에 보여줍니다.',
    body: <ReadoutPanel />,
    defaultOpen: true,
  },
  {
    id: 'level',
    title: '입력 레벨 미터',
    hint: 'rms(dBFS) 바 + 수치',
    help: '마이크 입력 음량(dBFS)을 막대로 표시합니다. 너무 작으면 소리로 인식되지 않습니다.',
    body: <InputLevelMeterPanel />,
    defaultOpen: true,
  },
];

// 점수 탭 — 스코어링(연습 채점).
const SCORE_PANELS: PanelMeta[] = [
  {
    id: 'scoring',
    title: '스코어링(연습 채점)',
    hint: '데모 멜로디 · 실시간 판정 · 결과 요약',
    help: '고른 목표 멜로디와 실제 음높이를 비교해 위/아래/정확을 실시간 판정하고, 정지하면 정확도·약점 구간을 요약합니다.',
    // dev 전용: 프로덕션 번들에선 placeholder만(연습 채점은 콘솔 검증 도구).
    body: IS_DEV ? <ScoringPanel /> : undefined,
    defaultOpen: true,
    hero: true,
  },
];

// 튜닝 탭 — config 슬라이더 + 세션 상태값.
const TUNING_PANELS: PanelMeta[] = [
  {
    id: 'config',
    title: 'config 튜닝',
    hint: 'silence/confidence/freq 슬라이더',
    help: '음정 분석 파라미터를 실시간으로 바꿔 결과가 어떻게 달라지는지 확인합니다.',
    // dev 전용: 프로덕션 번들에선 placeholder만(런타임 튜닝은 dev 도구).
    body: IS_DEV ? <ConfigTuningPanel /> : undefined,
    defaultOpen: true,
  },
  {
    id: 'session',
    title: '세션 상태',
    hint: '알고리즘 · 상태 · captureRate · fps',
    help: '현재 세션 상태와 알고리즘·캡처레이트·fps를 보여줍니다. 시작/정지는 하단 고정 바에서 합니다.',
    body: <SessionControlPanel />,
    defaultOpen: true,
  },
];

// 진단 탭 — 진단/성능 + 이벤트 로그.
const DIAGNOSTICS_PANELS: PanelMeta[] = [
  {
    id: 'diagnostics',
    title: '진단/성능',
    hint: 'p95 · overrun · dropout · thermal',
    help: '엔진이 한 프레임을 분석하는 데 걸린 시간과 끊김·발열 같은 성능 상태를 보여줍니다.',
    body: <DiagnosticsPanel />,
    defaultOpen: true,
  },
  {
    id: 'events',
    title: '이벤트 로그/인스펙터',
    hint: 'raw 스트림 · 타입 필터 · JSON',
    help: '엔진이 보낸 모든 이벤트를 순서대로 기록합니다. 오류·끊김은 색으로 강조됩니다.',
    body: <EventLogPanel />,
    defaultOpen: true,
  },
];

const TAB_PANELS: Record<Exclude<TabId, 'help'>, PanelMeta[]> = {
  live: LIVE_PANELS,
  score: SCORE_PANELS,
  tuning: TUNING_PANELS,
  diagnostics: DIAGNOSTICS_PANELS,
};

const TABS: { id: TabId; label: string }[] = [
  { id: 'live', label: '라이브' },
  { id: 'score', label: '점수' },
  { id: 'tuning', label: '튜닝' },
  { id: 'diagnostics', label: '진단' },
  { id: 'help', label: '도움말' },
];

const readStoredTab = (): string | null => {
  try {
    return localStorage.getItem(TAB_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredTab = (id: TabId): void => {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, id);
  } catch {
    // localStorage 불가(사파리 프라이빗 등) — 무시하고 메모리 상태만 유지.
  }
};

/**
 * 프레임이 어디서 오는지 — 세 상태다.
 *   native      : 실기기 엔진(MPM). 실제 마이크 + 실제 엔진
 *   browser mic : 실제 마이크지만 추정기는 JS ACF — **엔진이 아니다**
 *   mock        : 가짜 음정 고정 스트림
 * 가운데를 `native`로 뭉개면 실기기와 다른 알고리즘의 숫자를 실기기 값처럼 읽게 된다.
 */
const BridgeBadge = () => {
  const native = isRunningInNativeApp();
  const { source } = useSessionControl();
  const mic = !native && source === 'mic';
  const tone = native ? STATUS_TONE.ok : STATUS_TONE.warn;
  const label = native
    ? 'native · 실데이터'
    : mic
      ? '브라우저 마이크 · ACF (엔진 아님)'
      : 'mock · 데모 데이터';

  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tone}`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {label}
      </span>
      <InfoTip label="데이터 출처 설명">
        {native
          ? '네이티브 엔진에 연결됨 — 실제 마이크 입력으로 분석한 실데이터입니다.'
          : mic
            ? '실제 마이크 입력입니다. 다만 브라우저에는 네이티브 엔진(MPM)이 없어 JS ACF로 분석합니다 — 같은 녹음에서 두 추정기의 음높이가 한 옥타브까지 엇갈릴 수 있으므로, 값 자체를 실기기와 비교하지 말고 "설정을 바꾸면 어떻게 반응하는지"를 보세요.'
            : '네이티브 앱 밖(브라우저)이고 소스가 “데모”라 가짜 음정을 흘립니다. 설정을 바꿔도 화면이 변하지 않습니다 — 실제로 튜닝해 보려면 세션 상태 카드에서 소스를 “내 목소리”로 바꾸세요.'}
      </InfoTip>
    </span>
  );
};

interface PanelCardProps {
  panel: PanelMeta;
}

const PanelCard = ({ panel }: PanelCardProps) => {
  const hero = panel.hero ?? false;
  const [open, setOpen] = useState(panel.defaultOpen);
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <section
      className={`rounded-2xl border bg-background ${
        hero
          ? 'border-primary/30 bg-primary/[0.02] shadow-sm'
          : 'border-border/40'
      }`}
    >
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          <span
            className={`font-display font-semibold tracking-tight ${
              hero ? 'text-base' : 'text-sm'
            }`}
          >
            {panel.title}
          </span>
          <span
            className={`text-xs text-muted-foreground transition-transform duration-200 ${
              open ? 'rotate-90' : ''
            }`}
          >
            ▸
          </span>
        </button>
        <InfoTip label={`${panel.title} 설명`}>{panel.help}</InfoTip>
        <button
          type="button"
          onClick={() => setHelpOpen((prev) => !prev)}
          aria-pressed={helpOpen}
          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition motion-safe:active:scale-95 ${
            helpOpen
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border/60 text-muted-foreground hover:bg-muted'
          }`}
        >
          도움말
        </button>
      </div>

      {open && (
        <div className="border-t border-border/30 px-4 py-4 text-sm text-muted-foreground">
          {helpOpen && (
            <p className="mb-3 rounded-lg bg-muted/50 px-3 py-2 text-xs leading-relaxed text-foreground">
              {panel.help}
            </p>
          )}
          {panel.body ?? (
            <div className="py-2">
              {panel.hint}
              <p className="mt-2 text-xs opacity-60">
                패널 본문은 후속 태스크에서 추가됩니다.
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// 탭 안 패널 그리드 — 데스크탑은 2열, 히어로 패널은 가로 폭 전체.
const PanelGrid = ({ panels }: { panels: PanelMeta[] }) => (
  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
    {panels.map((panel) => (
      <div key={panel.id} className={panel.hero ? 'lg:col-span-2' : ''}>
        <PanelCard panel={panel} />
      </div>
    ))}
  </div>
);

// 도움말 탭 — 첫 사용 가이드 + 용어집(접이식·닫음 기억은 각 컴포넌트가 유지).
const HelpTab = () => (
  <div className="flex flex-col gap-4">
    <FirstTimeGuide />
    <Glossary />
  </div>
);

interface TabBarProps {
  active: TabId;
  onSelect: (id: TabId) => void;
}

// 상단 가로 스크롤 탭바 — 활성 강조 + 키보드(←→ Home End)/aria 접근성.
const TabBar = ({ active, onSelect }: TabBarProps) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const next = nextTabIndex(index, event.key, TABS.length);
    if (next === index) {
      return;
    }
    event.preventDefault();
    onSelect(TABS[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="콘솔 탭"
      className="flex gap-1 overflow-x-auto pb-1"
    >
      {TABS.map((tab, index) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full border px-4 text-sm font-medium transition motion-safe:active:scale-95 ${
              selected
                ? 'border-primary/40 bg-primary text-primary-foreground'
                : 'border-border/60 text-muted-foreground hover:bg-muted'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export const DebugConsolePage = () => {
  const [active, setActive] = useState<TabId>(() =>
    resolveInitialTab(readStoredTab()),
  );

  const selectTab = (id: TabId) => {
    setActive(id);
    writeStoredTab(id);
  };

  return (
    <SessionControlProvider>
      <PullToRefresh>
        <div className="mx-auto min-h-[100dvh] max-w-5xl px-4 pb-6 pt-[calc(var(--safe-area-top,0px)+1.5rem)]">
          <DebugConsoleHeader />

          {/* 탭바는 상단 sticky — 세션 sticky 바(하단)와 충돌 없이 항상 닿는다(#28). */}
          <div className="sticky top-[var(--safe-area-top,0px)] z-30 -mx-4 border-b border-border/30 bg-background/90 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/75">
            <TabBar active={active} onSelect={selectTab} />
          </div>

          {/*
           * 모든 탭 패널을 mount 유지하고 비활성은 `hidden`(display:none)으로만 가린다(#28).
           * 탭을 바꿔도 그래프 기록·이벤트 구독·스코어링 상태가 끊기지 않는다. `hidden` 클래스
           * 가 display 유틸을 이기도록 grid는 PanelGrid 안쪽에 두고 래퍼엔 display 유틸을 안 쓴다.
           */}
          {TABS.map((tab) => {
            const isActive = tab.id === active;
            return (
              <div
                key={tab.id}
                role="tabpanel"
                id={`tabpanel-${tab.id}`}
                aria-labelledby={`tab-${tab.id}`}
                hidden={!isActive}
                className={isActive ? 'mt-4' : 'hidden'}
              >
                {tab.id === 'help' ? (
                  <HelpTab />
                ) : (
                  <PanelGrid panels={TAB_PANELS[tab.id]} />
                )}
              </div>
            );
          })}

          {/* 전역: 어느 탭에서든 시작/정지에 항상 닿는 sticky 세션 바(#25, #27). */}
          <SessionStickyBar />
        </div>
      </PullToRefresh>
    </SessionControlProvider>
  );
};

const DebugConsoleHeader = () => {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="font-display text-xl font-bold tracking-tight">
            엔진 디버그 콘솔
          </h1>
          {/* 콘솔이 곧 앱 — 프로덕션 번들에 들어가도 dev 도구임을 명시한다. */}
          <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-600">
            DEV CONSOLE
          </span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          web ↔ mobile ↔ native 풀체인 음정 엔진 관찰·제어·튜닝·검증
        </p>
      </div>
      <BridgeBadge />
    </header>
  );
};
